// src/components/EditEventPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db, storage, functions } from '../firebase';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faCalendarAlt,
    faSave,
    faCheck,
    faExclamationCircle,
    faAlignLeft,
    faClock,
    faRepeat,
    faCamera,
    faTimes
} from '@fortawesome/free-solid-svg-icons';

import './FormPage.css';

function EditEventPage() {
    const { eventId } = useParams();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [eventDate, setEventDate] = useState('');
    const [eventTime, setEventTime] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [isRecurringEvent, setIsRecurringEvent] = useState(false);
    const [seriesId, setSeriesId] = useState(null);
    const [editScope, setEditScope] = useState('this');
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [existingPhotoPath, setExistingPhotoPath] = useState(null);
    const [photoRemoved, setPhotoRemoved] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const navigate = useNavigate();
    const { currentUser, isAdmin, userOrgId } = useAuth();

    const MAX_PHOTO_SIZE = 10 * 1024 * 1024; // 10MB

    // Fetch event data
    useEffect(() => {
        const fetchEvent = async () => {
            if (!eventId || !userOrgId) {
                setIsLoading(false);
                return;
            }

            try {
                const eventDoc = await getDoc(doc(db, 'events', eventId));
                if (eventDoc.exists()) {
                    const data = eventDoc.data();

                    // Verify org membership
                    if (data.orgId !== userOrgId) {
                        setError("You don't have permission to edit this event.");
                        setIsLoading(false);
                        return;
                    }

                    setTitle(data.title || '');
                    setDescription(data.description || '');
                    setIsRecurringEvent(!!data.isRecurring);
                    setSeriesId(data.seriesId || null);

                    // Load existing photo
                    if (data.photo) {
                        setExistingPhotoPath(data.photo);
                        try {
                            const photoUrl = await getDownloadURL(ref(storage, data.photo));
                            setPhotoPreview(photoUrl);
                        } catch (photoErr) {
                            console.warn("Could not load existing photo:", photoErr);
                        }
                    }

                    // Parse dateTime (use local date/time consistently)
                    if (data.dateTime) {
                        const date = data.dateTime instanceof Timestamp
                            ? data.dateTime.toDate()
                            : new Date(data.dateTime);
                        const y = date.getFullYear();
                        const m = String(date.getMonth() + 1).padStart(2, '0');
                        const d = String(date.getDate()).padStart(2, '0');
                        setEventDate(`${y}-${m}-${d}`);
                        setEventTime(date.toTimeString().slice(0, 5));
                    }
                } else {
                    setError("Event not found.");
                }
            } catch (err) {
                console.error("Error fetching event:", err);
                setError("Failed to load event details.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchEvent();
    }, [eventId, userOrgId]);

    const handlePhotoSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file.');
            return;
        }
        if (file.size > MAX_PHOTO_SIZE) {
            setError('Photo must be under 10MB.');
            return;
        }
        setPhotoFile(file);
        setPhotoPreview(URL.createObjectURL(file));
        setPhotoRemoved(false);
        setError(null);
    };

    const handlePhotoRemove = () => {
        setPhotoFile(null);
        if (photoPreview && !existingPhotoPath) {
            URL.revokeObjectURL(photoPreview);
        }
        setPhotoPreview(null);
        setPhotoRemoved(true);
    };

    // Cleanup object URL on unmount
    React.useEffect(() => {
        return () => {
            if (photoPreview && !existingPhotoPath) URL.revokeObjectURL(photoPreview);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sanitizeFilename = (name) => name.replace(/[^a-zA-Z0-9._-]/g, '_');

    const uploadEventPhoto = async (file, orgId, docId, isSeries) => {
        const folder = isSeries ? 'eventSeries' : 'event';
        const storagePath = `${orgId}/${folder}/${docId}/${sanitizeFilename(file.name)}`;
        const storageRef = ref(storage, storagePath);
        return new Promise((resolve, reject) => {
            const uploadTask = uploadBytesResumable(storageRef, file);
            uploadTask.on('state_changed',
                (snapshot) => {
                    const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                    setUploadProgress(progress);
                },
                (error) => reject(error),
                () => resolve(storagePath)
            );
        });
    };

    // Get minimum date (today)
    const getMinDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const validateForm = () => {
        setError(null);
        if (!title.trim()) {
            setError("Event title cannot be empty.");
            return false;
        }
        if (!eventDate) {
            setError("Please select an event date.");
            return false;
        }
        if (!eventTime) {
            setError("Please select an event time.");
            return false;
        }
        // Validate date is in the future for single-event edits
        if (editScope === 'this') {
            const selectedDateTime = new Date(`${eventDate}T${eventTime}`);
            if (selectedDateTime < new Date()) {
                setError("Event date and time must be in the future.");
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validateForm() || !currentUser || !isAdmin || !userOrgId) {
            if (!currentUser) setError("You must be logged in to edit an event.");
            if (!isAdmin) setError("You must be an admin to edit events.");
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccessMessage(null);

        try {
            // Combine date and time into a single ISO string
            const dateTimeString = `${eventDate}T${eventTime}`;
            const isoString = new Date(dateTimeString).toISOString();

            // Determine photo handling for the payload
            let photoPathValue;
            const isSeries = isRecurringEvent && seriesId && editScope !== 'this';
            const docIdForUpload = isSeries ? seriesId : eventId;

            if (photoFile) {
                // Upload new photo
                try {
                    photoPathValue = await uploadEventPhoto(photoFile, userOrgId, docIdForUpload, !!isSeries);
                } catch (uploadErr) {
                    console.error("Error uploading photo:", uploadErr);
                    setError("Failed to upload photo. Please try again.");
                    setIsSaving(false);
                    return;
                }
            } else if (photoRemoved) {
                photoPathValue = null; // Signal removal
            }
            // If neither photoFile nor photoRemoved, photoPathValue stays undefined (no change)

            const updateEventCallable = httpsCallable(functions, 'updateEvent');
            const payload = {
                eventId,
                title: title.trim(),
                description: description.trim(),
                dateTime: isoString,
            };

            if (photoPathValue !== undefined) {
                payload.photoPath = photoPathValue;
            }

            if (isRecurringEvent && seriesId) {
                payload.scope = editScope;
            }

            await updateEventCallable(payload);

            setSuccessMessage("Event updated successfully!");

            setTimeout(() => {
                navigate(`/events/${eventId}`, { state: { message: 'Event updated successfully.' } });
            }, 1500);
        } catch (err) {
            console.error("Error updating event:", err);
            setError("Failed to update event. Please try again.");
        } finally {
            if (!successMessage) {
                setIsSaving(false);
            }
        }
    };

    // Redirect non-admins
    if (!isAdmin) {
        return (
            <div className="form-page">
                <div className="form-page-header">
                    <div className="container">
                        <div className="form-page-header-content">
                            <div className="form-page-header-left">
                                <Link to="/events" className="form-back-btn" title="Back to Events">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Link>
                                <div>
                                    <h1 className="form-page-title">Access Denied</h1>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="form-card">
                        <div className="form-card-body">
                            <div className="form-alert form-alert-danger">
                                <FontAwesomeIcon icon={faExclamationCircle} className="form-alert-icon" />
                                <div className="form-alert-content">
                                    Only administrators can edit events.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Loading state
    if (isLoading) {
        return (
            <div className="form-page">
                <div className="form-page-header">
                    <div className="container">
                        <div className="form-page-header-content">
                            <div className="form-page-header-left">
                                <Link to="/events" className="form-back-btn" title="Back to Events">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Link>
                                <div>
                                    <h1 className="form-page-title">Edit Event</h1>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="form-card">
                        <div className="form-card-body" style={{ textAlign: 'center', padding: '3rem' }}>
                            <Spinner animation="border" variant="primary" />
                            <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading event...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const formInputsDisabled = isSaving || !!successMessage;

    return (
        <div className="form-page">
            {/* Page Header */}
            <div className="form-page-header">
                <div className="container">
                    <div className="form-page-header-content">
                        <div className="form-page-header-left">
                            <Link to={`/events/${eventId}`} className="form-back-btn" title="Back to Event">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="form-page-title">Edit Event</h1>
                                <p className="form-page-subtitle">Update event details</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                {/* Form Card */}
                <div className="form-card">
                    <div className="form-card-body">
                        {/* Error Alert */}
                        {error && (
                            <div className="form-alert form-alert-danger">
                                <FontAwesomeIcon icon={faExclamationCircle} className="form-alert-icon" />
                                <div className="form-alert-content">{error}</div>
                            </div>
                        )}

                        {/* Success Alert */}
                        {successMessage && (
                            <div className="form-alert form-alert-success">
                                <FontAwesomeIcon icon={faCheck} className="form-alert-icon" />
                                <div className="form-alert-content">{successMessage}</div>
                            </div>
                        )}

                        {!successMessage && (
                            <form onSubmit={handleSubmit} noValidate>
                                {/* Edit Scope for Recurring Events */}
                                {isRecurringEvent && seriesId && (
                                    <div className="form-section">
                                        <h3 className="form-section-title">
                                            <FontAwesomeIcon icon={faRepeat} style={{ marginRight: '0.5rem' }} />
                                            Edit Scope
                                        </h3>
                                        <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '0.75rem' }}>
                                            This is a recurring event. What would you like to edit?
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            {[
                                                { value: 'this', label: 'This event only', desc: 'Detach from series and edit freely' },
                                                { value: 'future', label: 'This and all future events', desc: 'Update the series from this date forward' },
                                                { value: 'all', label: 'All events in series', desc: 'Update all occurrences' },
                                            ].map(opt => (
                                                <label key={opt.value} style={{
                                                    display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                                                    padding: '0.5rem 0.75rem', borderRadius: '8px', cursor: 'pointer',
                                                    background: editScope === opt.value ? '#eff6ff' : '#f8fafc',
                                                    border: `1px solid ${editScope === opt.value ? '#bfdbfe' : '#e2e8f0'}`
                                                }}>
                                                    <input
                                                        type="radio" name="editScope"
                                                        value={opt.value}
                                                        checked={editScope === opt.value}
                                                        onChange={(e) => setEditScope(e.target.value)}
                                                        disabled={formInputsDisabled}
                                                        style={{ marginTop: '0.25rem' }}
                                                    />
                                                    <div>
                                                        <div style={{ fontWeight: 500 }}>{opt.label}</div>
                                                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{opt.desc}</div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Event Info Section */}
                                <div className="form-section">
                                    <h3 className="form-section-title">
                                        <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '0.5rem' }} />
                                        Event Information
                                    </h3>

                                    <div className="form-field">
                                        <label className="form-field-label">
                                            Event Title <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-field-input"
                                            placeholder="Enter event title"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                            disabled={formInputsDisabled}
                                            maxLength={200}
                                            autoFocus
                                        />
                                    </div>

                                    <div className="form-field">
                                        <label className="form-field-label">
                                            <FontAwesomeIcon icon={faAlignLeft} style={{ marginRight: '0.5rem' }} />
                                            Description
                                        </label>
                                        <textarea
                                            className="form-field-input form-field-textarea"
                                            placeholder="Enter event description (optional)"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            disabled={formInputsDisabled}
                                            rows={4}
                                            maxLength={2000}
                                        />
                                    </div>

                                    {/* Photo Upload */}
                                    <div className="form-field">
                                        <label className="form-field-label">
                                            <FontAwesomeIcon icon={faCamera} style={{ marginRight: '0.5rem' }} />
                                            Event Photo
                                        </label>
                                        {photoPreview ? (
                                            <div className="event-photo-preview-container">
                                                <img src={photoPreview} alt="Event preview" className="event-photo-preview" />
                                                <button
                                                    type="button"
                                                    className="event-photo-remove-btn"
                                                    onClick={handlePhotoRemove}
                                                    disabled={formInputsDisabled}
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </button>
                                            </div>
                                        ) : (
                                            <label className="event-photo-upload-area" style={{ cursor: formInputsDisabled ? 'default' : 'pointer' }}>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handlePhotoSelect}
                                                    disabled={formInputsDisabled}
                                                    style={{ display: 'none' }}
                                                />
                                                <FontAwesomeIcon icon={faCamera} style={{ fontSize: '1.5rem', color: '#94a3b8' }} />
                                                <span style={{ color: '#64748b', fontSize: '0.9rem' }}>Click to add a photo (max 10MB)</span>
                                            </label>
                                        )}
                                        {uploadProgress > 0 && uploadProgress < 100 && (
                                            <div className="event-photo-progress">
                                                <div className="event-photo-progress-bar" style={{ width: `${uploadProgress}%` }} />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Date & Time Section */}
                                <div className="form-section">
                                    <h3 className="form-section-title">
                                        <FontAwesomeIcon icon={faClock} style={{ marginRight: '0.5rem' }} />
                                        Date & Time
                                    </h3>

                                    <div className="form-row">
                                        <div className="form-field form-field-half">
                                            <label className="form-field-label">
                                                Date <span className="required">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                className="form-field-input"
                                                value={eventDate}
                                                onChange={(e) => setEventDate(e.target.value)}
                                                disabled={formInputsDisabled}
                                                min={getMinDate()}
                                            />
                                        </div>
                                        <div className="form-field form-field-half">
                                            <label className="form-field-label">
                                                Time <span className="required">*</span>
                                            </label>
                                            <input
                                                type="time"
                                                className="form-field-input"
                                                value={eventTime}
                                                onChange={(e) => setEventTime(e.target.value)}
                                                disabled={formInputsDisabled}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="form-actions">
                                    <button
                                        type="submit"
                                        className="form-submit-btn form-submit-btn-success"
                                        disabled={formInputsDisabled}
                                    >
                                        {isSaving ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" />
                                                Saving Changes...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faSave} />
                                                Save Changes
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}

                        {successMessage && (
                            <div className="form-actions" style={{ borderTop: 'none', paddingTop: 0 }}>
                                <button
                                    type="button"
                                    className="form-submit-btn"
                                    onClick={() => navigate(`/events/${eventId}`)}
                                >
                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                    View Event
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditEventPage;
